/* eslint-disable no-nested-ternary */
import { useUrlGenerator } from '@folklore/routes';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useCallback, useMemo } from 'react';

import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';

import styles from '../../../styles/partials/sport-questions-selector.module.scss';

const propTypes = {
    className: PropTypes.string,
    currentQuestion: AppPropTypes.question,
    game: AppPropTypes.participation,
    participation: AppPropTypes.participation,
    onClickQuestion: PropTypes.func,
};

const defaultProps = {
    className: null,
    currentQuestion: null,
    game: null,
    participation: null,
    onClickQuestion: null,
};

function SportQuestionsSelector({
    className,
    currentQuestion,
    game,
    participation,
    onClickQuestion,
}) {
    const route = useUrlGenerator();
    const themeClassName = useThemeClassName(styles);
    const { id: participationId = null } = participation || {};
    const { id: gameId, questionsGroups } = game || {};
    const questions = useMemo(() =>
        questionsGroups.reduce(
            (all, { questions: groupQuestions = [] }) => [...all, ...groupQuestions],
            [],
        ),
    );

    const { id: currentQuestionId = null, index: currentQuestionIndex = null } =
        currentQuestion || {};
    const { id: currentQuestionGroupId = null, period: currentQuestionGroupPeriod } =
        questionsGroups.find(
            ({ questions: groupQuestions = [] }) =>
                groupQuestions.findIndex(({ id }) => id === currentQuestionId) !== -1,
        ) || {};

    const pillsGroups = useMemo(
        () =>
            questionsGroups.reduce((groups, group) => {
                const {
                    id = null,
                    type = null,
                    period = null,
                    questions: groupQuestions = [],
                } = group;
                const pills = groupQuestions.map(({ id: questionId, slug, index }) => ({
                    id: questionId,
                    selected: questionId === currentQuestionId,
                    trivia: type === 'trivia',
                    url:
                        participationId !== null
                            ? route('participation.question', {
                                  participation: participationId,
                                  question: slug,
                              })
                            : route('game.question', {
                                  game: gameId,
                                  question: slug,
                              }),
                    index: index + 1,
                }));
                const existingGroupPeriodIndex =
                    period !== null
                        ? groups.findIndex(({ period: groupPeriod }) => groupPeriod === period)
                        : -1;
                const { pills: existingPills = null, ...existingGroupPeriod } =
                    existingGroupPeriodIndex !== -1 ? groups[existingGroupPeriodIndex] : {};
                if (existingPills !== null && existingPills.length <= 1) {
                    return [
                        ...groups.slice(0, existingGroupPeriodIndex),
                        {
                            ...existingGroupPeriod,
                            selected: period === currentQuestionGroupPeriod,
                            pills: [...existingPills, ...pills],
                        },
                        ...groups.slice(existingGroupPeriodIndex + 1),
                    ];
                }
                return [
                    ...groups,
                    {
                        id,
                        period,
                        selected: id === currentQuestionGroupId,
                        pills,
                    },
                ];
            }, []),
        [
            questionsGroups,
            currentQuestionId,
            currentQuestionGroupId,
            currentQuestionGroupPeriod,
            participationId,
            gameId,
        ],
    );

    const hasParticipation = participationId !== null;

    const previousUrl = useMemo(() => {
        if (currentQuestionIndex > 0) {
            const { slug } = questions[currentQuestionIndex - 1];
            return hasParticipation
                ? route('participation.question', {
                      participation: participationId,
                      question: slug,
                  })
                : route('game.question', {
                      game: gameId,
                      question: slug,
                  });
        }
        return null;
    }, [currentQuestionIndex, hasParticipation, questions]);

    const nextUrl = useMemo(() => {
        if (currentQuestionIndex < questions.length - 1) {
            const { slug } = questions[currentQuestionIndex + 1];
            return hasParticipation
                ? route('participation.question', {
                      participation: participationId,
                      question: slug,
                  })
                : route('game.question', {
                      game: gameId,
                      question: slug,
                  });
        }
        return null;
    }, [currentQuestionIndex, hasParticipation, questions]);

    const onLeftClick = useCallback(() => {
        if (onClickQuestion !== null) {
            onClickQuestion();
        }
    }, [onClickQuestion, currentQuestionIndex, questions]);

    const onRightClick = useCallback(() => {
        if (onClickQuestion !== null) {
            onClickQuestion();
        }
    }, [onClickQuestion, questions]);

    return (
        <div className={classNames([styles.container, themeClassName, className])}>
            <Button
                className={styles.button}
                onClick={onLeftClick}
                disabled={previousUrl === null}
                href={previousUrl}
            >
                <FontAwesomeIcon icon={faChevronLeft} />
            </Button>
            <div className={styles.periods}>
                {pillsGroups.map(({ id: groupId, selected: groupSelected, pills }) => (
                    <div
                        key={`group-${groupId}`}
                        className={classNames([
                            styles.period,
                            {
                                [styles.selected]: groupSelected,
                            },
                        ])}
                    >
                        {pills.map(({ id, selected, trivia, url, index }) => (
                            <Button
                                className={classNames([
                                    styles.question,
                                    {
                                        [styles.selected]: selected,
                                        [styles.isTrivia]: trivia,
                                    },
                                ])}
                                key={`question-${id}`}
                                href={url}
                            >
                                {index}
                            </Button>
                        ))}
                    </div>
                ))}
            </div>
            <Button
                className={styles.button}
                onClick={onRightClick}
                disabled={nextUrl === null}
                href={nextUrl}
            >
                <FontAwesomeIcon icon={faChevronRight} />
            </Button>
        </div>
    );
}

SportQuestionsSelector.propTypes = propTypes;
SportQuestionsSelector.defaultProps = defaultProps;

export default SportQuestionsSelector;
